/**
 * GA4 e-commerce event wiring for the Dawn cart/product JS.
 *
 * Reads item data that the Liquid templates already render server-side
 * (`data-ga4-item` on cart rows and on <product-info>) and taps into the
 * theme's existing pub/sub bus (assets/pubsub.js, PUB_SUB_EVENTS in
 * assets/constants.js) and a small custom event dispatched from
 * assets/cart.js — no changes to product-form.js were needed since it
 * already publishes PUB_SUB_EVENTS.cartUpdate with the raw /cart/add.js
 * response.
 *
 * Fires: view_item, add_to_cart, remove_from_cart, begin_checkout.
 * `purchase` cannot be fired from theme code — Shopify's hosted checkout
 * and order-status page are not part of this repo. See
 * assets/ga4-checkout-pixel.js for that event.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  function toDecimal(cents) {
    if (cents === undefined || cents === null) return undefined;
    return Math.round(cents) / 100;
  }

  function pushEcommerceEvent(eventName, ecommerce) {
    // GA4 recommendation: clear the previous ecommerce object so fields
    // from an earlier event don't leak into this one.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
  }

  // Maps a `/cart/add.js` (single-line) or `/cart.js` line item to the
  // GA4 standard `items[]` entry shape.
  function mapAjaxCartLine(item) {
    return {
      item_id: item.sku || String(item.variant_id || item.id),
      item_name: item.product_title || item.title,
      item_variant: item.variant_title || undefined,
      item_brand: item.vendor || undefined,
      price: toDecimal(item.final_price != null ? item.final_price : item.price),
      quantity: item.quantity,
    };
  }

  // Maps the `data-ga4-item` JSON payload rendered server-side (already
  // in decimal price form — see the Liquid `capture` blocks that build it).
  function readGa4ItemFromElement(el) {
    if (!el || !el.dataset.ga4Item) return null;
    try {
      return JSON.parse(el.dataset.ga4Item);
    } catch (e) {
      return null;
    }
  }

  // Cached full cart (/cart.js shape), kept warm so begin_checkout can
  // fire synchronously the instant the checkout button is clicked instead
  // of racing an async fetch against the browser navigating away.
  var cartCache = null;

  function fetchCart() {
    var pending =
      typeof CartItems !== 'undefined' && CartItems.fetchCartData
        ? CartItems.fetchCartData()
        : fetch((window.routes && routes.cart_url ? routes.cart_url : '/cart') + '.js').then(function (r) {
            return r.json();
          });
    return pending
      .then(function (cart) {
        if (cart) cartCache = cart;
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  var GA4 = {
    viewItem: function (item) {
      pushEcommerceEvent('view_item', {
        currency: item.currency,
        value: item.price,
        items: [item],
      });
    },

    addToCart: function (item, currency) {
      pushEcommerceEvent('add_to_cart', {
        currency: currency,
        value: Math.round((item.price || 0) * item.quantity * 100) / 100,
        items: [item],
      });
    },

    removeFromCart: function (item, currency) {
      pushEcommerceEvent('remove_from_cart', {
        currency: currency,
        value: Math.round((item.price || 0) * item.quantity * 100) / 100,
        items: [item],
      });
    },

    beginCheckout: function () {
      if (!cartCache || !Array.isArray(cartCache.items) || cartCache.items.length === 0) return;
      pushEcommerceEvent('begin_checkout', {
        currency: cartCache.currency,
        value: toDecimal(cartCache.total_price),
        items: cartCache.items.map(mapAjaxCartLine),
      });
    },
  };

  window.GA4 = GA4;

  // --- view_item: any element rendered with data-ga4-view-item ----------
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-ga4-view-item]').forEach(function (el) {
      var item = readGa4ItemFromElement(el);
      if (item) GA4.viewItem(item);
    });

    // Seed the cart cache so begin_checkout works even if the user lands
    // straight on a page and clicks checkout without triggering a cart
    // mutation first (e.g. cart already had items from an earlier visit).
    fetchCart();
  });

  // --- add_to_cart: PUB_SUB_EVENTS.cartUpdate from product-form.js ------
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source === 'product-form' && event.cartData && event.cartData.id) {
        var item = mapAjaxCartLine(event.cartData);
        GA4.addToCart(item, event.cartData.currency || (cartCache && cartCache.currency));
      }
      // Keep the cart cache fresh after every mutation (add, quantity
      // change, remove) regardless of source.
      fetchCart();
    });
  }

  // --- add_to_cart / remove_from_cart: quantity changes in the cart -----
  // Dispatched from assets/cart.js `updateQuantity()`, which knows both
  // the previous and new quantity for the changed line.
  document.addEventListener('ga4:cart-line-change', function (evt) {
    var detail = evt.detail || {};
    var previousItem = detail.previousItem;
    if (!previousItem) return;

    var delta = detail.newQuantity - previousItem.quantity;
    if (delta === 0) return;

    var changedItem = Object.assign({}, previousItem, { quantity: Math.abs(delta) });
    if (delta < 0) {
      GA4.removeFromCart(changedItem, previousItem.currency);
    } else {
      GA4.addToCart(changedItem, previousItem.currency);
    }
  });

  // --- begin_checkout: any "Checkout" button in the cart page or drawer -
  document.addEventListener('click', function (evt) {
    if (evt.target.closest('.cart__checkout-button')) {
      GA4.beginCheckout();
    }
  });
})();
